import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { usePublicEvent } from '@/features/event/api'
import { formatClock, useCountdown } from '@/features/voting/countdown'
import { formatTime } from '@/lib/datetime'
import { applyEventTheme } from '@/lib/theme'

import { useScreenState } from '../api'
import { OverlayView } from '../screen/OverlayView'
import { ResultsBoard } from '../screen/ResultsBoard'
import { RunwayCard } from '../screen/RunwayCard'
import { VoteBoards } from '../screen/VoteBoards'
import { WinnersCelebration } from '../screen/WinnersCelebration'
import type { PublicCakeMoment, PublicProgramItem, ScreenState } from '../types'

/**
 * L'écran du restaurant.
 *
 * Contraintes réelles : une télé à quatre mètres, une salle sombre, personne
 * pour la surveiller. D'où le parti pris — trois informations, très grandes,
 * et une page qui se remet toute seule après une coupure réseau.
 *
 * Ce que l'écran montre en « MAINTENANT » est ce que l'équipe a réellement
 * lancé, pas ce que l'horaire prévoyait : une soirée a toujours du retard.
 *
 * Une seule vue à la fois — la télé ne se partage pas. Quand plusieurs
 * choses sont vraies ensemble, la plus forte l'emporte (voir `screenMode`).
 */

type ScreenMode =
  | 'overlay'
  | 'cake'
  | 'results'
  | 'vote'
  | 'runway'
  | 'proclamation'
  | 'programme'

/**
 * Ce que la télé montre, quand plusieurs choses sont vraies à la fois.
 *
 * 1. **Le calque du MC** (`overlay` : profils des candidats, gagnant désigné)
 *    — un geste volontaire, posé pour quelques minutes : il passe devant tout.
 * 2. **Le Cake Moment** — l'étape du gâteau tourne : la télé devient un décor.
 * 3. **Les scores publiés** — une seule page : le palmarès en tête, puis tous
 *    les concours et tous les tours, jusqu'à ce qu'on retire la publication.
 *    La fin de soirée appartient au palmarès.
 * 4. **Un vote ouvert** — les nommés en photo et le décompte, sans bouton.
 * 5. **Le défilé** — la personne que le MC a désignée, à côté du programme.
 * 6. **La proclamation** — un titre proclamé, scores pas encore publiés :
 *    « Vainqueur · Miss », en géant, sous une guirlande. Après le vote et le
 *    défilé : si Master défile encore quand Miss est proclamée, la salle
 *    regarde le podium ; la fête revient dès que le tour se termine.
 * 7. **Le programme** — maintenant, ensuite, plus tard.
 *
 * Tout vient du serveur : la télé n'a qu'à lire, jamais à deviner.
 */
function screenMode(state: ScreenState | undefined): ScreenMode {
  if (!state) return 'programme'
  if (state.overlay) return 'overlay'
  if (state.cake) return 'cake'
  if ((state.results ?? []).some((contest) => contest.rows.length > 0)) return 'results'
  if ((state.open_vote_boards ?? []).length > 0) return 'vote'
  if (state.on_stage) return 'runway'
  if (state.winners.length > 0) return 'proclamation'
  return 'programme'
}

/** La clé du fondu : un changement de vue — ou de calque — refait l'entrée. */
function modeKey(state: ScreenState, mode: ScreenMode): string {
  if (mode === 'overlay' && state.overlay) return `overlay-${state.overlay.published_at}`
  if (mode === 'vote') {
    return `vote-${(state.open_vote_boards ?? []).map((board) => board.vote_category_id).join('-')}`
  }
  if (mode === 'proclamation') {
    // Master proclamé après Miss : la page refait son entrée, avec les deux.
    return `proclamation-${state.winners.map((winner) => winner.candidate_id ?? winner.name).join('-')}`
  }
  return mode
}
export function ScreenPage() {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const key = searchParams.get('key')
  const slug = searchParams.get('slug')

  const eventQuery = usePublicEvent(slug ?? undefined)
  const screenQuery = useScreenState(key, slug)
  const timezone = eventQuery.data?.timezone ?? 'Europe/Berlin'

  useEffect(() => {
    if (eventQuery.data) applyEventTheme(eventQuery.data.theme)
  }, [eventQuery.data])

  useEffect(() => {
    document.title = screenQuery.data?.event_name ?? 'LeYAMO'
  }, [screenQuery.data?.event_name])

  if (!key) {
    return <ScreenMessage title={t('screen.noKey')} body={t('screen.noKeyHelp')} />
  }

  if (screenQuery.isError) {
    // Un écran accroché au mur n'a personne pour cliquer « réessayer » :
    // la requête repart d'elle-même, on explique simplement l'attente.
    return <ScreenMessage title={t('screen.unavailable')} body={t('screen.unavailableHelp')} />
  }

  const state = screenQuery.data
  const mode = screenMode(state)

  if (state && mode !== 'programme') {
    // Un seul cadre pour toutes les vues du concours : l'écran passe de l'une
    // à l'autre en fondu, sans clignoter sur une page vide entre les deux.
    const fullBleed = mode === 'overlay' || mode === 'cake' || mode === 'proclamation'
    return (
      <main
        className={
          'flex h-dvh flex-col overflow-hidden bg-bg-base ' +
          (mode === 'cake' ? '' : 'px-[3vw] py-[3vh]')
        }
      >
        {fullBleed ? null : (
          <header className="flex shrink-0 items-baseline justify-between gap-8">
            <h1 className="truncate font-display text-[min(4.4vh,2.6vw)] text-champagne-300">
              {state.event_name}
            </h1>
            <Clock timezone={timezone} locale={i18n.language} compact />
          </header>
        )}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={modeKey(state, mode)}
            className={'flex min-h-0 flex-1 flex-col ' + (fullBleed ? '' : 'pt-[2.5vh]')}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {mode === 'overlay' && state.overlay ? (
              <OverlayView overlay={state.overlay} />
            ) : mode === 'cake' && state.cake ? (
              <CakeTakeover cake={state.cake} />
            ) : mode === 'results' ? (
              <ResultsBoard contests={state.results ?? []} winners={state.winners} />
            ) : mode === 'vote' ? (
              <VoteBoards boards={state.open_vote_boards ?? []} serverTime={state.server_time} />
            ) : mode === 'runway' && state.on_stage ? (
              <RunwayLayout state={state} timezone={timezone} locale={i18n.language} />
            ) : mode === 'proclamation' ? (
              <WinnersCelebration winners={state.winners} eventName={state.event_name} />
            ) : null}
          </motion.div>
        </AnimatePresence>
      </main>
    )
  }

  return (
    <main className="flex min-h-dvh flex-col bg-bg-base px-10 py-8">
      <header className="flex items-baseline justify-between gap-8">
        <h1 className="font-display text-heading-lg text-champagne-300">
          {state?.event_name ?? ''}
        </h1>
        <Clock timezone={timezone} locale={i18n.language} />
      </header>

      <div className="flex flex-1 flex-col justify-center gap-12 py-10">
        <section>
          <p className="overline text-content-muted">{t('screen.now')}</p>
          {state?.now ? (
            <>
              <p className="mt-3 font-display text-[clamp(3rem,9vw,7rem)] leading-[1.05] text-content-primary">
                {state.now.title}
              </p>
              {state.now.description ? (
                <p className="mt-5 max-w-5xl text-[clamp(1.1rem,2vw,1.75rem)] text-content-secondary">
                  {state.now.description}
                </p>
              ) : null}
            </>
          ) : (
            <p className="mt-3 font-display text-[clamp(2.5rem,7vw,5rem)] text-content-muted">
              {t('screen.waiting')}
            </p>
          )}
        </section>

        {/* Le vote, tel que les statuts le disent : ouvert, on invite à voter ;
            gelé, l'invitation disparaît d'elle-même. Aucun score ici. */}
        {state && state.open_votes.length > 0 ? (
          // Sur une télé, le décompte se range à droite de l'invitation : il
          // reste énorme sans pousser « Ensuite » hors de l'écran.
          <section className="rounded-lg border-2 border-ember-400 px-8 py-6 lg:flex lg:items-center lg:justify-between lg:gap-12">
            <div className="min-w-0">
              <p className="font-display text-[clamp(2rem,5vw,4rem)] leading-tight text-ember-300">
                {t('screen.voteOpen')}
              </p>
              <p className="mt-2 text-[clamp(1.1rem,2.2vw,2rem)] text-content-primary">
                {state.open_votes.join(' · ')}
              </p>
              <p className="mt-2 text-[clamp(1rem,1.8vw,1.5rem)] text-content-secondary">
                {t('screen.voteHow')}
              </p>
            </div>
            {(state.vote_timers ?? []).length > 0 ? (
              <div className="shrink-0 space-y-6">
                {(state.vote_timers ?? []).map((timer) => (
                  <VoteTimer
                    key={timer.name}
                    name={state.vote_timers && state.vote_timers.length > 1 ? timer.name : null}
                    endsAt={timer.ends_at}
                    serverTime={state.server_time}
                  />
                ))}
              </div>
            ) : null}
          </section>
        ) : state?.tiebreak_running ? (
          <section className="rounded-lg border border-line-subtle px-8 py-6">
            <p className="font-display text-[clamp(1.75rem,4vw,3rem)] text-champagne-300">
              {t('screen.tiebreakRunning')}
            </p>
          </section>
        ) : null}

        {/* Les titres proclamés n'apparaissent pas ici : dès le premier, la
            télé passe à la proclamation en grand (voir `screenMode`). */}

        <section className="border-t border-line-subtle pt-8">
          <p className="overline text-content-muted">{t('screen.next')}</p>
          {state?.next ? (
            <div className="mt-3 flex flex-wrap items-baseline gap-6">
              <p className="font-display text-[clamp(1.75rem,4vw,3rem)] text-content-primary">
                {state.next.title}
              </p>
              {state.next.starts_at ? (
                <span className="font-mono text-[clamp(1.25rem,2.5vw,2rem)] tabular-nums text-champagne-300">
                  {formatTime(state.next.starts_at, i18n.language, timezone)}
                </span>
              ) : null}
              {state.countdown_seconds !== null && state.countdown_seconds !== undefined ? (
                <Countdown seconds={state.countdown_seconds} />
              ) : null}
            </div>
          ) : (
            <p className="mt-3 text-[clamp(1.25rem,3vw,2rem)] text-content-muted">
              {t('screen.nothingNext')}
            </p>
          )}
        </section>

        {state && state.later.length > 0 ? (
          <section className="border-t border-line-subtle pt-8">
            <p className="overline text-content-muted">{t('screen.later')}</p>
            <ul className="mt-4 flex flex-wrap gap-x-12 gap-y-3">
              {state.later.map((item) => (
                <LaterItem
                  key={item.id}
                  item={item}
                  timezone={timezone}
                  locale={i18n.language}
                />
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </main>
  )
}

/**
 * Le LeYAMO Cake Moment, plein écran.
 *
 * Toute la soirée, cet écran est un tableau d'affichage. Pendant cette étape,
 * il devient un décor : le gâteau entre en salle, les téléphones se lèvent, et
 * personne ne cherche l'heure du round suivant. D'où le parti pris — une image,
 * un nom, une phrase, et rien d'autre à l'écran.
 */
function CakeTakeover({ cake }: { cake: PublicCakeMoment }) {
  return (
    <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden bg-bg-base px-10 text-center">
      {cake.media_url ? (
        <>
          <img src={cake.media_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
          {/* Voile : la photo d'un gâteau est claire, le texte doit rester lisible. */}
          <div className="absolute inset-0 bg-black/70" />
        </>
      ) : null}

      <div className="relative">
        <p className="text-[clamp(4rem,14vw,12rem)] leading-none">🎂</p>
        <h1 className="mt-6 font-display text-[clamp(2.5rem,8vw,6rem)] leading-[1.05] text-champagne-300">
          {cake.name}
        </h1>
        {cake.message ? (
          <p className="mx-auto mt-8 max-w-4xl text-[clamp(1.25rem,3vw,2.5rem)] text-content-primary">
            {cake.message}
          </p>
        ) : null}
        {cake.description ? (
          <p className="mx-auto mt-5 max-w-3xl text-[clamp(1rem,2vw,1.5rem)] text-content-secondary">
            {cake.description}
          </p>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Le défilé : le programme à gauche, la personne qui passe à droite.
 *
 * Le programme reste visible (la salle veut savoir ce qui suit), mais réduit
 * à l'essentiel ; le profil prend la place. Quand le MC passe au candidat
 * suivant, la carte glisse et la nouvelle entre — la salle voit qu'on a
 * changé de personne, même de loin.
 */
function RunwayLayout({
  state,
  timezone,
  locale,
}: {
  state: ScreenState
  timezone: string
  locale: string
}) {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const onStage = state.on_stage
  if (!onStage) return null

  return (
    <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_minmax(0,1.9fr)] gap-[3vw]">
      <div className="flex min-h-0 min-w-0 flex-col justify-center gap-[5vh] overflow-hidden">
        <section>
          <p className="overline text-[min(2.4vh,1.5vw)] text-content-muted">{t('screen.now')}</p>
          <p className="mt-[1vh] break-words font-display text-[min(6vh,3.4vw)] leading-[1.08] text-content-primary">
            {state.now?.title ?? t('screen.waiting')}
          </p>
        </section>
        <section className="border-t border-line-subtle pt-[3vh]">
          <p className="overline text-[min(2.4vh,1.5vw)] text-content-muted">{t('screen.next')}</p>
          {state.next ? (
            <>
              <p className="mt-[1vh] break-words font-display text-[min(4.4vh,2.6vw)] leading-tight text-content-primary">
                {state.next.title}
              </p>
              {state.next.starts_at ? (
                <p className="mt-[0.6vh] font-mono text-[min(3.4vh,2vw)] tabular-nums text-champagne-300">
                  {formatTime(state.next.starts_at, locale, timezone)}
                </p>
              ) : null}
            </>
          ) : (
            <p className="mt-[1vh] text-[min(3.2vh,1.9vw)] text-content-muted">
              {t('screen.nothingNext')}
            </p>
          )}
        </section>
      </div>

      <div className="relative min-h-0 min-w-0">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${onStage.round.id}-${onStage.candidate.id}`}
            className="absolute inset-0"
            initial={reduce ? { opacity: 0 } : { opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, x: -60 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <RunwayCard onStage={onStage} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

function LaterItem({
  item,
  timezone,
  locale,
}: {
  item: PublicProgramItem
  timezone: string
  locale: string
}) {
  return (
    <li className="text-[clamp(1rem,1.8vw,1.5rem)] text-content-secondary">
      {item.starts_at ? (
        <span className="mr-3 font-mono tabular-nums text-content-muted">
          {formatTime(item.starts_at, locale, timezone)}
        </span>
      ) : null}
      {item.title}
    </li>
  )
}

/**
 * Le décompte du vote, lisible du fond de la salle.
 *
 * Il tourne seul entre deux lectures du serveur, corrigé de l'écart entre
 * l'horloge de la télé et celle du serveur. À zéro, il dit « temps écoulé » et
 * reste là : le vote est encore ouvert, c'est le MC qui le ferme — et
 * l'encadré disparaît alors avec lui.
 */
function VoteTimer({
  name,
  endsAt,
  serverTime,
}: {
  /** Le nom du vote, seulement quand plusieurs décomptent à la fois. */
  name: string | null
  endsAt: string
  serverTime: string
}) {
  const { t } = useTranslation()
  const remaining = useCountdown(endsAt, serverTime)
  if (remaining === null) return null

  if (remaining === 0) {
    // Le décompte s'arrête à 0:00 et le dit en toutes lettres. Le vote, lui,
    // reste ouvert : la salle peut encore voter jusqu'au geste du MC.
    return (
      <div className="mt-6 flex flex-wrap items-baseline gap-x-10 gap-y-2 border-t border-[color-mix(in_srgb,var(--ember-400)_40%,transparent)] pt-6 lg:mt-0 lg:border-t-0 lg:pt-0">
        {name ? (
          <p className="w-full text-[clamp(1rem,1.8vw,1.5rem)] text-content-muted">{name}</p>
        ) : null}
        <p className="font-mono text-[clamp(2.5rem,6vw,5.5rem)] leading-none tabular-nums text-content-muted">
          {formatClock(0)}
        </p>
        <p role="status" className="font-display text-[clamp(2.5rem,6vw,5.5rem)] leading-none text-champagne-300">
          {t('voteTimer.screenElapsed')}
        </p>
      </div>
    )
  }

  return (
    <div className="mt-6 border-t border-[color-mix(in_srgb,var(--ember-400)_40%,transparent)] pt-6 lg:mt-0 lg:border-t-0 lg:pt-0">
      <p className="text-[clamp(1rem,1.8vw,1.5rem)] text-content-muted">
        {name ? `${name} — ` : ''}
        {t('voteTimer.screenRemaining')}
      </p>
      <p
        role="timer"
        aria-live="off"
        className={
          'font-mono text-[clamp(4rem,11vw,10rem)] leading-none tabular-nums ' +
          (remaining <= 10
            ? 'text-ember-300 motion-safe:animate-pulse'
            : 'text-content-primary')
        }
      >
        {formatClock(remaining)}
      </p>
    </div>
  )
}

/** Horloge locale de la salle — le seul élément que le serveur ne rythme pas. */
function Clock({
  timezone,
  locale,
  compact = false,
}: {
  timezone: string
  locale: string
  /** Dans les vues du concours : plus discrète, la place va aux candidats. */
  compact?: boolean
}) {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <time
      className={
        'font-mono tabular-nums text-content-primary ' +
        (compact ? 'text-[min(4vh,2.4vw)]' : 'text-[clamp(1.5rem,3vw,2.5rem)]')
      }
    >
      {formatTime(now.toISOString(), locale, timezone)}
    </time>
  )
}

function Countdown({ seconds }: { seconds: number }) {
  const { t } = useTranslation()
  const [remaining, setRemaining] = useState(seconds)

  // Le décompte repart du chiffre serveur à chaque rafraîchissement : il ne
  // dérive pas si l'horloge de la télé est fausse.
  useEffect(() => {
    setRemaining(seconds)
    const timer = window.setInterval(() => setRemaining((value) => Math.max(value - 1, 0)), 1000)
    return () => window.clearInterval(timer)
  }, [seconds])

  const minutes = Math.floor(remaining / 60)
  if (minutes <= 0) return null

  return (
    <span className="text-[clamp(1rem,2vw,1.5rem)] text-content-muted">
      {t('screen.inMinutes', { count: minutes })}
    </span>
  )
}

function ScreenMessage({ title, body }: { title: string; body: string }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-bg-base px-10 text-center">
      <h1 className="font-display text-[clamp(2rem,5vw,3.5rem)] text-content-primary">{title}</h1>
      <p className="max-w-3xl text-[clamp(1rem,2vw,1.5rem)] text-content-secondary">{body}</p>
    </main>
  )
}
